'use client';

import React from 'react';
import { useRouter, useHashRouter, getPortal } from '@/lib/router';
import { PublicLayout, AffiliateLayout, TenantLayout, AdminLayout } from '@/components/layouts';
import {
  LandingPage, AboutPage, PricingPage, LoginPage, RegisterPage,
  CheckoutPage, SetupWorkspacePage,
} from '@/components/client-pages';
import {
  AffiliateDashboardPage, AffiliateReferralsPage,
  AffiliateCommissionsPage, AffiliateWithdrawalsPage,
} from '@/components/affiliate-pages';
import {
  TenantDashboardPage, TenantAgentsPage, TenantAgentDetailPage,
  TenantChannelsPage, TenantWASessionsPage, TenantCustomersPage,
  TenantKnowledgeBasePage, TenantConversationsPage, TenantBusinessPage,
  TenantIntegrationsPage, TenantToolApprovalsPage, TenantSubscriptionPage,
  TenantTeamPage,
} from '@/components/tenant-pages';
import {
  AdminDashboardPage, AdminTenantsPage, AdminUsersPage,
  AdminSubscriptionsPage, AdminPlansPage, AdminPromosPage,
  AdminAffiliatesPage, AdminWAGatewayPage, AdminAIProvidersPage,
  AdminToolRegistryPage, AdminAuditLogsPage, AdminSecurityPage,
  AdminSystemSettingsPage,
} from '@/components/admin-pages';
import {
  AdminTenantDetailPage, AdminTenantCreatePage,
  AdminUserDetailPage, AdminUserCreatePage,
  TenantBusinessDetailPage, TenantBusinessCreatePage,
  TenantKnowledgeBaseDetailPage, TenantKnowledgeBaseCreatePage,
} from '@/components/detail-pages';

function Router() {
  useHashRouter();
  const currentPath = useRouter((s) => s.currentPath);
  const matchRoute = useRouter((s) => s.matchRoute);

  // ---- Public Pages ----
  const publicRoutes: Record<string, React.ReactNode> = {
    '/': <LandingPage />,
    '/about': <AboutPage />,
    '/pricing': <PricingPage />,
    '/login': <LoginPage />,
    '/register': <RegisterPage />,
    '/forgot-password': <LoginPage />,
    '/reset-password': <LoginPage />,
    '/setup-workspace': <SetupWorkspacePage />,
  };

  // ---- Affiliate Pages ----
  const affiliateRoutes: Record<string, React.ReactNode> = {
    '/affiliate/dashboard': <AffiliateDashboardPage />,
    '/affiliate/referrals': <AffiliateReferralsPage />,
    '/affiliate/commissions': <AffiliateCommissionsPage />,
    '/affiliate/withdrawals': <AffiliateWithdrawalsPage />,
  };

  // ---- Tenant Pages (static) ----
  const tenantRoutes: Record<string, React.ReactNode> = {
    '/tenant/dashboard': <TenantDashboardPage />,
    '/tenant/agents': <TenantAgentsPage />,
    '/tenant/channels': <TenantChannelsPage />,
    '/tenant/wa-sessions': <TenantWASessionsPage />,
    '/tenant/customers': <TenantCustomersPage />,
    '/tenant/knowledge': <TenantKnowledgeBasePage />,
    '/tenant/conversations': <TenantConversationsPage />,
    '/tenant/business': <TenantBusinessPage />,
    '/tenant/integrations': <TenantIntegrationsPage />,
    '/tenant/tool-approvals': <TenantToolApprovalsPage />,
    '/tenant/subscription': <TenantSubscriptionPage />,
    '/tenant/team': <TenantTeamPage />,
  };

  // ---- Admin Pages (static) ----
  const adminRoutes: Record<string, React.ReactNode> = {
    '/admin/dashboard': <AdminDashboardPage />,
    '/admin/tenants': <AdminTenantsPage />,
    '/admin/users': <AdminUsersPage />,
    '/admin/subscriptions': <AdminSubscriptionsPage />,
    '/admin/plans': <AdminPlansPage />,
    '/admin/promos': <AdminPromosPage />,
    '/admin/affiliates': <AdminAffiliatesPage />,
    '/admin/wa-gateway': <AdminWAGatewayPage />,
    '/admin/ai-providers': <AdminAIProvidersPage />,
    '/admin/tool-registry': <AdminToolRegistryPage />,
    '/admin/audit-logs': <AdminAuditLogsPage />,
    '/admin/security': <AdminSecurityPage />,
    '/admin/settings': <AdminSystemSettingsPage />,
  };

  // Resolve page content based on current path
  const portal = getPortal(currentPath);
  let pageContent: React.ReactNode = null;

  if (portal === 'public') {
    if (matchRoute('/checkout/[id]')) {
      pageContent = <CheckoutPage />;
    } else {
      pageContent = publicRoutes[currentPath] || <LandingPage />;
    }
  } else if (portal === 'affiliate') {
    pageContent = affiliateRoutes[currentPath] || <AffiliateDashboardPage />;
  } else if (portal === 'tenant') {
    // Dynamic tenant routes
    if (matchRoute('/tenant/agents/[id]')) {
      pageContent = <TenantAgentDetailPage />;
    } else if (matchRoute('/tenant/knowledge/[id]')) {
      pageContent = <TenantKnowledgeBaseDetailPage />;
    } else if (currentPath === '/tenant/knowledge/new') {
      pageContent = <TenantKnowledgeBaseCreatePage />;
    } else if (matchRoute('/tenant/business/products/[id]')) {
      pageContent = <TenantBusinessDetailPage />;
    } else if (currentPath === '/tenant/business/products/new') {
      pageContent = <TenantBusinessCreatePage />;
    } else {
      pageContent = tenantRoutes[currentPath] || <TenantDashboardPage />;
    }
  } else if (portal === 'admin') {
    // Dynamic admin routes
    if (matchRoute('/admin/tenants/[id]')) {
      pageContent = <AdminTenantDetailPage />;
    } else if (currentPath === '/admin/tenants/new') {
      pageContent = <AdminTenantCreatePage />;
    } else if (matchRoute('/admin/users/[id]')) {
      pageContent = <AdminUserDetailPage />;
    } else if (currentPath === '/admin/users/new') {
      pageContent = <AdminUserCreatePage />;
    } else {
      pageContent = adminRoutes[currentPath] || <AdminDashboardPage />;
    }
  }

  // Wrap in layout
  let layout: React.ReactNode;
  if (portal === 'public') {
    layout = <PublicLayout>{pageContent}</PublicLayout>;
  } else if (portal === 'affiliate') {
    layout = <AffiliateLayout>{pageContent}</AffiliateLayout>;
  } else if (portal === 'tenant') {
    layout = <TenantLayout>{pageContent}</TenantLayout>;
  } else if (portal === 'admin') {
    layout = <AdminLayout>{pageContent}</AdminLayout>;
  } else {
    layout = <PublicLayout><LandingPage /></PublicLayout>;
  }

  return layout;
}

export default function Home() {
  return <Router />;
}
